import { FC } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Button,
  FormGenerator,
  ModalWrapper,
  ScriptValueDialog as NewScriptValueDialog,
} from '@microservices/wiskey-react-components'
import { Box, CircularProgress, Grid } from '@mui/material'

import { AddCommandsDialog } from '@pages/EntityCreateOrEdit/components/AddActionDialog/AddCommandsDialog'
import { getJSScriptConfig } from '@pages/EntityCreateOrEdit/components/AddActionDialog/helpers/getJSScriptConfig'

import { CommandPickerController } from '@components/hookFormControllers/CommandPickerController'
import { ScriptValueDialog } from '@components/ScriptValueDialog'

import { CATEGORIES, ENTITY_TYPES_REQUEST, MODAL_TYPE } from '@constants'
import {
  ActionRow,
  Dictionary,
  EntityType,
  GETAction,
  GETEntityColumn,
  GETShareColumn,
  ModalType,
  MutationType,
  PayloadCreateAction,
  ShareColumnType,
  Table,
  TimelineActionMeta,
} from '@types'

import { useAddActionDialog } from './hooks'

export type AddActionDialogType = {
  type: ENTITY_TYPES_REQUEST
  actionEventsCategoryCode?: CATEGORIES
  actionTable: Table<ActionRow>
  bindingValuesAction?: Dictionary[]
  columns: GETEntityColumn[]
  contextMenuEventsCategoryCode?: CATEGORIES
  createAction: MutationType<PayloadCreateAction, GETAction>
  currentEntity?: EntityType
  currentRow?: GETShareColumn | null
  editAction: MutationType<PayloadCreateAction, GETAction>
  entityCode?: string
  isActionLoading?: boolean
  isDisableActionEventsFiltering?: boolean
  modalType: ModalType
  objectCode: string
  onCloseModal: (isDirty?: boolean) => void
  onSetModalProperty?: (type: ShareColumnType) => void // Отображать все доступные события
  showDialog: boolean
  tooltipEventsCategoryCode?: CATEGORIES
  variablesConfig?: {
    entityType?: ENTITY_TYPES_REQUEST
    entityId?: string | number
  }
  meta?: TimelineActionMeta
}

export const AddActionDialog: FC<AddActionDialogType> = props => {
  const { onCloseModal, showDialog, modalType, objectCode, isActionLoading, type, meta } = props

  const { t } = useTranslation()

  const actionProps = useAddActionDialog(props)

  const {
    state: {
      isDirty,
      watchRedefinitionCommands,
      isLoading,
      methods,
      watchJson,
      watchJsValue,
      watchActionCode,
      watchEvent,
      currentNameScriptDialog,
      isOpenScriptValueDialog,
      showActionCommandsModal,
      watchCommand,
    },
    handlers: { handleShowActionCommandsModal, handleSave, setIsOpenScriptValueDialog },
    data: {
      actionInputs,
      actionValueInputs,
      actionJSONValueInputs,
      actionViewInputs,
      isOpenFormAction,
      isOpenViewAction,
    },
  } = actionProps

  const { setValue } = methods

  const jsScriptConfig = getJSScriptConfig({
    type,
    eventCode: watchEvent,
    actionCode: watchActionCode,
    command: watchCommand,
    meta,
  })

  const handleSaveScriptValueDialog = (value: string) => {
    if (!currentNameScriptDialog) return

    setValue(currentNameScriptDialog as 'jsValue', value, { shouldDirty: true })
  }

  const handleCloseScriptValueDialog = () => {
    setIsOpenScriptValueDialog(false)
  }

  return (
    <ModalWrapper
      disabledSave={!isDirty || isActionLoading}
      isShow={showDialog}
      title={`${t(`addDialog.${modalType}`)} Action`}
      width={1000}
      containerStyle={{
        px: 0.5,
        py: 0.5,
      }}
      onClose={onCloseModal}
      onSave={handleSave}
    >
      {isLoading && modalType === MODAL_TYPE.EDIT ? (
        <Grid container alignItems={'center'} justifyContent={'center'} my={3}>
          <CircularProgress />
        </Grid>
      ) : (
        <Box maxHeight={600} overflow='auto' pr={1} pt={1}>
          <FormProvider {...methods}>
            <form>
              {showActionCommandsModal && (
                <AddCommandsDialog
                  commands={actionProps.state.watchRedefinitionCommands}
                  objectCode={objectCode}
                  onChangeCommands={actionProps.handlers.handleChangeRedefinitionCommands}
                  onCloseDialog={actionProps.handlers.handleCloseActionCommandsModal}
                />
              )}
              <FormGenerator inputs={actionInputs} mainGridSpacing={0} />
              {!watchJson && isOpenFormAction && (
                <Grid display='flex'>
                  <FormGenerator isRow inputs={actionValueInputs} mainGridSpacing={0} />
                  <Button
                    disabled={!objectCode}
                    sx={{ ml: 1, width: 200 }}
                    variant='outlined'
                    onClick={handleShowActionCommandsModal}
                  >
                    {t('actionInputs.formCommands.label', {
                      mode: watchRedefinitionCommands?.length ? 'edit' : 'add',
                    })}
                  </Button>
                </Grid>
              )}
              {!watchJson && isOpenViewAction && (
                <FormGenerator isRow inputs={actionViewInputs} mainGridSpacing={0} />
              )}
              <FormGenerator inputs={actionJSONValueInputs} mainGridSpacing={0} />
              <CommandPickerController
                isAnotherObjectWithRequestData
                isSingle
                isTypeHidden
                commandName='name'
                jsScriptConfig={jsScriptConfig.command}
                name='command'
                objectCode={objectCode}
              />
            </form>
            {isOpenScriptValueDialog &&
              (jsScriptConfig.jsField ? (
                <NewScriptValueDialog
                  {...jsScriptConfig.jsField}
                  isShow={isOpenScriptValueDialog}
                  value={watchJsValue}
                  onClose={handleCloseScriptValueDialog}
                  onSave={handleSaveScriptValueDialog}
                />
              ) : (
                <ScriptValueDialog
                  windowBoundaries
                  isShow={isOpenScriptValueDialog}
                  modalType={'edit'}
                  objectFields={[]}
                  value={watchJsValue}
                  onClose={handleCloseScriptValueDialog}
                  onSave={handleSaveScriptValueDialog}
                />
              ))}
          </FormProvider>
        </Box>
      )}
    </ModalWrapper>
  )
}
